import { findNodePosition } from '@/lib/tiptap-utils';
import { getBasicContentSchema } from '@/lib/zy-tiptap-utils';
import { Node } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { ZyExerciseNodeView } from './zyexercise-node-view';
import { ZyProblemNodeView } from './zyproblem-node-view';
import { ZyProblemSolutionNodeView } from './zyproblemsolution-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zyExercise: {
            insertZyExercise: () => ReturnType;
            addZyProblem: (node: PMNode) => ReturnType;
        };
        zyProblem: {
            insertZyProblemSolution: (node: PMNode, pos?: number) => ReturnType;
        };
    }
}

export const ZyExerciseNodeHeadless = Node.create({
    name: 'zyExercise',

    content: 'zySetup? zyProblem+',

    group: 'block',

    isolating: true,

    draggable: true,

    // Define attributes using addAttributes
    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => {
                    return element.getAttribute('id');
                },
                renderHTML: attributes => {
                    if (!attributes.id) {
                        return {};
                    }
                    return { id: attributes.id };
                },
            },
            caption: {
                default: null,
                parseHTML: element => {
                    return element.getAttribute('caption');
                },
                renderHTML: attributes => {
                    if (!attributes.caption) {
                        return {};
                    }
                    return { caption: attributes.caption };
                },
            },
            hidden: {
                default: false,
                parseHTML: element => {
                    return element.getAttribute('hidden') === 'true';
                },
                renderHTML: attributes => {
                    if (!attributes.hidden) {
                        return {};
                    }
                    return { hidden: 'true' };
                },
            },
        };
    },

    // Parse HTML to this node
    parseHTML() {
        return [
            {
                tag: 'zyExercise',
            },
        ];
    },

    // Render this node to HTML
    renderHTML({ node, HTMLAttributes }) {
        return [
            'zyExercise',
            {
                ...HTMLAttributes,
                id: node.attrs.id,
                caption: node.attrs.caption,
                hidden: node.attrs.hidden,
            },
            0,
        ];
    },

    addCommands() {
        return {
            insertZyExercise:
                () =>
                ({ chain }) => {
                    return chain()
                        .focus()
                        .insertContent({
                            type: 'zyExercise',
                            attrs: {
                                caption: '',
                                hidden: false,
                            },
                            content: [
                                {
                                    type: 'zySetup',
                                    content: [
                                        {
                                            type: 'paragraph',
                                            attrs: {
                                                textAlign: null,
                                            },
                                        },
                                    ],
                                },
                                {
                                    type: 'zyProblem',
                                    content: [
                                        {
                                            type: 'paragraph',
                                            attrs: {
                                                textAlign: null,
                                            },
                                        },
                                        {
                                            type: 'zyProblemSolution',
                                            attrs: {
                                                studentview: 'false',
                                            },
                                            content: [
                                                {
                                                    type: 'paragraph',
                                                    attrs: {
                                                        textAlign: null,
                                                    },
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        })
                        .command(({ commands }) => {
                            return commands.selectDescendantNodeContent('zySetup');
                        })
                        .run();
                },
            addZyProblem:
                (node: PMNode, pos?: number) =>
                ({ editor, chain }) => {
                    const targetPos = pos ?? findNodePosition({ editor, node })?.pos;
                    if (targetPos === undefined) {
                        return false;
                    }

                    const insertPos = targetPos + node.nodeSize - 1;

                    return chain()
                        .focus()
                        .insertContentAt(insertPos, {
                            type: 'zyProblem',
                            content: [
                                {
                                    type: 'paragraph',
                                    attrs: {
                                        textAlign: null,
                                    },
                                },
                                {
                                    type: 'zyProblemSolution',
                                    attrs: {
                                        studentview: 'false',
                                    },
                                    content: [
                                        {
                                            type: 'paragraph',
                                            attrs: {
                                                textAlign: null,
                                            },
                                        },
                                    ],
                                },
                            ],
                        })
                        .selectDescendantNodeContent('zyProblem', insertPos)
                        .run();
                },
        };
    },
});

export const ZySetupNodeHeadless = Node.create({
    name: 'zySetup',

    isolating: true,

    content: function () {
        return getBasicContentSchema({ additionalContentTypes: ['zyInlineEnum'] });
    },

    group: 'block zyExerciseContent',

    parseHTML() {
        return [
            {
                tag: 'zySetup',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zySetup', HTMLAttributes, 0];
    },
});

export const ZyProblemNodeHeadless = Node.create({
    name: 'zyProblem',

    isolating: true,

    content: function () {
        return `${getBasicContentSchema({ additionalContentTypes: ['zyInlineEnum'] })} zyProblemSolution?`;
    },

    group: 'block zyExerciseContent',

    parseHTML() {
        return [
            {
                tag: 'zyProblem',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyProblem', HTMLAttributes, 0];
    },

    addCommands() {
        return {
            insertZyProblemSolution:
                (node: PMNode, pos?: number) =>
                ({ chain, editor, state }) => {
                    // Check if node already has a zyProblemSolution child
                    let hasSolution = false;
                    node.forEach(child => {
                        if (child.type.name === 'zyProblemSolution') {
                            hasSolution = true;
                        }
                    });

                    if (hasSolution) {
                        return false;
                    }

                    const targetPos = pos ?? findNodePosition({ editor, node })?.pos;
                    if (targetPos === undefined) {
                        return false;
                    }

                    const zyProblemSolutionContent = state.schema.nodeFromJSON({
                        type: 'zyProblemSolution',
                        attrs: {
                            studentview: false,
                        },
                        content: [
                            {
                                type: 'paragraph',
                                attrs: {
                                    textAlign: null,
                                },
                            },
                        ],
                    });

                    return chain()
                        .focus()
                        .insertContentAt(targetPos + node.nodeSize - 1, zyProblemSolutionContent)
                        .selectDescendantNodeContent('zyProblemSolution', targetPos + node.nodeSize - 1)
                        .run();
                },
        };
    },
});

export const ZyProblemSolutionNodeHeadless = Node.create({
    name: 'zyProblemSolution',

    isolating: true,

    content: function () {
        return getBasicContentSchema({ additionalContentTypes: ['zyInlineEnum'] });
    },

    group: 'block zyExerciseContent',

    addAttributes() {
        return {
            studentview: {
                default: false,
                parseHTML: element => {
                    return element.getAttribute('studentview') === 'true';
                },
                renderHTML: attributes => {
                    if (!attributes.studentview) {
                        return {};
                    }
                    return { studentview: 'true' };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'zyProblemSolution',
            },
        ];
    },

    renderHTML({ node, HTMLAttributes }) {
        return [
            'zyProblemSolution',
            {
                ...HTMLAttributes,
                studentview: node.attrs.studentview,
            },
            0,
        ];
    },
});

export const ZyProblemSolutionNode = ZyProblemSolutionNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyProblemSolutionNodeView);
    },
});

export const ZyProblemNode = ZyProblemNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyProblemNodeView);
    },
});

export const ZySetupNode = ZySetupNodeHeadless.extend({
    addNodeView() {
        return () => {
            const dom = document.createElement('div');
            dom.className = 'mb-2';

            const contentDOM = document.createElement('div');
            dom.appendChild(contentDOM);

            return {
                dom,
                contentDOM,
            };
        };
    },
});

export const ZyExerciseNode = ZyExerciseNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyExerciseNodeView, { trackNodeViewPosition: true });
    },
});
